import moment from 'moment';
import { AssignmentType } from '../../../api/Inventory/Entities/InventoryEntities';

export const MAX_LOT_NUMBER_LENGTH = 15;
export const MAX_MOVE_ITEM_QUANTITY = 999;

// El tema del proyecto deja gris el borde de un campo con error hasta que recibe foco;
// para un campo obligatorio eso no alcanza como aviso.
export const MISSING_COMMENT_STYLE = { borderColor: '#FF4A55' };

const LOT_PATTERN = new RegExp(`^[A-Za-z0-9]{1,${MAX_LOT_NUMBER_LENGTH}}$`);
const DIGITS_PATTERN = /^\d+$/;

interface MedicationOption {
    presentation?: string | null;
    drug?: string | null;
}

interface StockMedicationOption extends MedicationOption {
    stockQuantity?: number | string | null;
}

interface DetailItem {
    id?: number | string | null;
}

type ExpirationValue = Date | string | null | undefined;

interface ReleaseOption {
    id?: number | string | null;
    lotNumber?: string | null;
    expirationDate?: ExpirationValue;
    [key: string]: unknown;
}

interface AddItemArgs {
    hasSelectedItem: boolean;
    lotNumber: unknown;
    expirationDate: unknown;
    quantity: unknown;
    maxQuantity?: number | null;
}

interface DispenseAddArgs {
    hasItem: boolean;
    hasRelease: boolean;
    hasKit?: boolean;
    quantity: unknown;
    availableStock?: number | null;
}

interface LotConsumption {
    [lotKey: string]: number;
}

interface ItemLotConsumption {
    [itemKey: string]: LotConsumption;
}

interface LotKits {
    [lotKey: string]: Array<string>;
}

interface ItemLotKits {
    [itemKey: string]: LotKits;
}

interface KitAssignmentRef {
    kitNumber?: number | string | null;
}

interface KitStockRow {
    assignment?: KitAssignmentRef | null;
    [key: string]: unknown;
}

interface CartItemRef {
    id?: number | string | null;
    inventoryItemId?: number | string | null;
}

interface ReleaseListInit {
    itemId?: number | string | null;
    filteredFrom?: ItemLotConsumption | null;
    lotOfferRule?: string | null;
    assignmentType?: string | null;
    assignId?: number | string | null;
    includeExpired?: boolean | null;
    onlyAvailable?: boolean | null;
    includeStock?: boolean | null;
    onlyWithStock?: boolean | null;
    showStock?: boolean | null;
}

interface MoveItemRow {
    item?: CartItemRef | null;
    release?: ReleaseOption | null;
    assignment?: KitAssignmentRef | null;
    quantity?: number | string | null;
    confirmedQuantity?: number | string | null;
    availableQuantity?: number | string | null;
    [key: string]: unknown;
}

interface ResolvedReleaseListInit {
    itemId: number | string | null | undefined;
    filteredFrom: LotConsumption | null;
    lotOfferRule: string;
    assignmentType: string;
    assignId: number | string | null;
    includeExpired: boolean;
    onlyAvailable: boolean;
    includeStock: boolean;
    onlyWithStock: boolean;
    showStock: boolean;
}

interface ClearSelectionArgs {
    hasSelection: boolean;
    cartItemCount: number;
}

export function buildMedicationLabel(option: MedicationOption | null | undefined): string {
    const presentation = option?.presentation ?? '';
    const drug = option?.drug;
    const hasDrug = drug != null && drug.toString().trim() !== '';
    return hasDrug ? `${presentation} (${drug})` : `${presentation}`;
}

export function buildMedicationLabelWithStock(option: StockMedicationOption | null | undefined): string {
    const presentationWithStock = `${option?.presentation ?? ''} (stock: ${option?.stockQuantity})`;
    return buildMedicationLabel({ presentation: presentationWithStock, drug: option?.drug });
}

export function isValidLotNumber(lotNumber: unknown): boolean {
    return typeof lotNumber === 'string' && LOT_PATTERN.test(lotNumber);
}

export function isValidQuantity(quantity: unknown): boolean {
    if (quantity === '' || quantity == null) return false;
    const value = Number(quantity);
    return Number.isInteger(value) && value >= 1;
}

export function normalizeQuantityInput(value: unknown): string | null {
    if (value === '' || value == null) return '';
    const text = value.toString();
    if (!DIGITS_PATTERN.test(text)) return null;
    const withoutLeadingZeros = text.replace(/^0+(?=\d)/, '');
    return withoutLeadingZeros;
}

export function isWithinQuantityLimit(quantity: unknown, maxQuantity: number | null | undefined): boolean {
    if (maxQuantity == null) return true;
    return Number(quantity) <= maxQuantity;
}

// `null` means "reject the keystroke and keep the current value".
export function resolveQuantityInput(value: unknown, maxQuantity?: number | null): string | null {
    const normalized = normalizeQuantityInput(value);
    if (normalized === null || normalized === '') return normalized;
    return isWithinQuantityLimit(normalized, maxQuantity) ? normalized : null;
}

export function resolveExpirationValue(v: unknown): Date | null {
    return moment.isMoment(v) ? v.toDate() : null;
}

export function hasExpirationDate(value: unknown): boolean {
    if (value == null || value === '') return false;
    if (value instanceof Date) return !Number.isNaN(value.getTime());
    return true;
}

export function resolveEffectiveLot(selectedRelease: ReleaseOption | null | undefined, typedLot: string): string {
    if (Number(selectedRelease?.id) > 0) return selectedRelease?.lotNumber ?? '';
    return typedLot || selectedRelease?.lotNumber || '';
}

export function resolveEffectiveExpiration(
    selectedRelease: ReleaseOption | null | undefined,
    typedExpiration: ExpirationValue
): ExpirationValue {
    if (Number(selectedRelease?.id) > 0) return selectedRelease?.expirationDate ?? null;
    return typedExpiration ?? selectedRelease?.expirationDate ?? null;
}

export function isAddItemEnabled({ hasSelectedItem, lotNumber, expirationDate, quantity, maxQuantity }: AddItemArgs): boolean {
    return hasSelectedItem
        && isValidLotNumber(lotNumber)
        && hasExpirationDate(expirationDate)
        && isValidQuantity(quantity)
        && isWithinQuantityLimit(quantity, maxQuantity);
}

export function isDispenseAddEnabled({ hasItem, hasRelease, hasKit = true, quantity, availableStock }: DispenseAddArgs): boolean {
    return hasItem
        && hasRelease
        && hasKit
        && isValidQuantity(quantity)
        && isWithinQuantityLimit(quantity, availableStock);
}

export function resolveSubmittedRelease(
    selectedRelease: ReleaseOption | null | undefined,
    lotNumber: string,
    expirationDate: ExpirationValue
): ReleaseOption {
    return { ...(selectedRelease ?? {}), lotNumber, expirationDate };
}

export function buildDetailRowKey(item: DetailItem | null | undefined, index: number): string {
    return `${item?.id ?? 'row'}-${index}`;
}

export function resolveReleaseListInit(initValue: ReleaseListInit | null | undefined): ResolvedReleaseListInit {
    const init = initValue ?? {};

    return {
        itemId: init.itemId,
        filteredFrom: selectItemConsumption(init.filteredFrom, init.itemId),
        lotOfferRule: init.lotOfferRule ?? LotOfferRule.WithRemainingStock,
        assignmentType: init.assignmentType ?? AssignmentType.General.Name,
        assignId: init.assignId ?? null,
        includeExpired: init.includeExpired ?? false,
        onlyAvailable: init.onlyAvailable ?? true,
        includeStock: init.includeStock ?? true,
        onlyWithStock: init.onlyWithStock ?? true,
        showStock: init.showStock ?? true,
    };
}

function toLotKey(lotNumber: unknown): string {
    return (lotNumber ?? '').toString().trim().toLowerCase();
}

function toItemKey(itemId: unknown): string {
    return (itemId ?? '').toString().trim();
}

function toKitKey(kitNumber: unknown): string {
    return (kitNumber ?? '').toString().trim().toLowerCase();
}

// `InventoryItemLight` mantiene `id` e `inventoryItemId` con el mismo valor, así que un
// renglón recién agregado y uno traído del servidor caen bajo la misma clave de ítem.
function toRowItemKey(item: CartItemRef | null | undefined): string {
    return toItemKey(item?.inventoryItemId ?? item?.id);
}

function accumulateByLot(
    items: Array<MoveItemRow> | null | undefined,
    excludedRow: MoveItemRow | null | undefined,
    quantityOf: (row: MoveItemRow) => number
): ItemLotConsumption {
    const consumption: ItemLotConsumption = {};

    (items ?? []).forEach(row => {
        if (row === excludedRow) return;
        const lotKey = toLotKey(row?.release?.lotNumber);
        if (lotKey === '') return;
        const itemKey = toRowItemKey(row?.item);
        const takenFromItem = consumption[itemKey] ?? {};
        takenFromItem[lotKey] = (takenFromItem[lotKey] ?? 0) + quantityOf(row);
        consumption[itemKey] = takenFromItem;
    });

    return consumption;
}

// Dos ítems distintos pueden compartir número de lote: el consumo de uno no dice nada
// sobre los lotes del otro.
export function selectItemConsumption(
    consumption: ItemLotConsumption | null | undefined,
    itemId: number | string | null | undefined
): LotConsumption | null {
    if (consumption == null) return null;
    return consumption[toItemKey(itemId)] ?? {};
}

export function buildLotConsumption(
    items: Array<MoveItemRow> | null | undefined,
    excludedRow?: MoveItemRow | null
): ItemLotConsumption {
    return accumulateByLot(items, excludedRow, row => Number(row?.confirmedQuantity) || 0);
}

// Los renglones de un carrito todavía sin confirmar tienen `confirmedQuantity` en 0:
// lo que ya reservan del lote es su `quantity`.
export function buildCartLotConsumption(
    items: Array<MoveItemRow> | null | undefined,
    excludedRow?: MoveItemRow | null
): ItemLotConsumption {
    return accumulateByLot(items, excludedRow, row => Number(row?.quantity) || 0);
}

// Una dispensa con número de kit reparte el lote kit por kit: lo que el carrito ya tomó
// de un lote no es una cantidad sino el conjunto de kits elegidos. El renglón de stock sin
// número de kit es una identidad más del lote (clave vacía), no la ausencia de reserva.
export function buildCartKitReservation(
    items: Array<MoveItemRow> | null | undefined,
    excludedRow?: MoveItemRow | null
): ItemLotKits {
    const reservation: ItemLotKits = {};

    (items ?? []).forEach(row => {
        if (row === excludedRow) return;
        const lotKey = toLotKey(row?.release?.lotNumber);
        if (lotKey === '') return;
        const kitKey = toKitKey(row?.assignment?.kitNumber);
        const itemKey = toRowItemKey(row?.item);
        const takenFromItem = reservation[itemKey] ?? {};
        const takenFromLot = takenFromItem[lotKey] ?? [];
        takenFromItem[lotKey] = takenFromLot.indexOf(kitKey) >= 0 ? takenFromLot : [...takenFromLot, kitKey];
        reservation[itemKey] = takenFromItem;
    });

    return reservation;
}

export function selectTakenKits(
    reservation: ItemLotKits | null | undefined,
    itemId: number | string | null | undefined,
    lotNumber: unknown
): Array<string> {
    if (reservation == null) return [];
    const takenFromItem = reservation[toItemKey(itemId)] ?? {};
    return takenFromItem[toLotKey(lotNumber)] ?? [];
}

export function resolveOfferedKits(
    stockRows: Array<KitStockRow> | null | undefined,
    takenKits: Array<string> | null | undefined
): Array<KitStockRow> {
    const available = stockRows ?? [];
    if (takenKits == null || takenKits.length === 0) return available;

    const takenKeys = takenKits.map(toKitKey);
    return available.filter(row => takenKeys.indexOf(toKitKey(row?.assignment?.kitNumber)) < 0);
}

export function resolveRemainingStock(
    release: ReleaseOption | null | undefined,
    consumption: LotConsumption | null | undefined
): number {
    const stock = Number(release?.stockQuantity) || 0;
    const consumed = consumption?.[toLotKey(release?.lotNumber)] ?? 0;
    return stock - consumed;
}

export function hasRemainingStock(
    release: ReleaseOption | null | undefined,
    consumption: LotConsumption | null | undefined
): boolean {
    return resolveRemainingStock(release, consumption) > 0;
}

export function isLotAlreadyTaken(
    release: ReleaseOption | null | undefined,
    consumption: LotConsumption | null | undefined
): boolean {
    const key = toLotKey(release?.lotNumber);
    return key !== '' && consumption != null && Object.prototype.hasOwnProperty.call(consumption, key);
}

// Un carrito en edición no permite repartir un lote entre varios renglones: para cambiar
// cantidades se borra el renglón y se vuelve a agregar. La confirmación de una dispensa,
// en cambio, sí reparte lo que queda del lote entre los renglones ya guardados.
export const LotOfferRule = {
    WithRemainingStock: 'with-remaining-stock',
    NotAlreadyTaken: 'not-already-taken',
};

// `stockQuantity` must stay the server's stock everywhere, so the consumption of the
// other rows is subtracted exactly once, by each consumer.
export function resolveOfferedReleases(
    releases: Array<ReleaseOption> | null | undefined,
    consumption: LotConsumption | null | undefined,
    rule: string = LotOfferRule.WithRemainingStock
): Array<ReleaseOption> {
    const available = releases ?? [];
    if (consumption == null) return available;

    if (rule === LotOfferRule.NotAlreadyTaken) {
        return available.filter(release => !isLotAlreadyTaken(release, consumption));
    }

    return available.filter(release => hasRemainingStock(release, consumption));
}

export function isConfirmationWithinAvailable(row: MoveItemRow | null | undefined, confirmation: unknown): boolean {
    if (confirmation == null) return false;
    return Number(confirmation) <= (Number(row?.availableQuantity) || 0);
}

export function resolveConfirmableQuantity(row: MoveItemRow | null | undefined): number {
    const requested = Number(row?.quantity) || 0;
    const available = Number(row?.availableQuantity) || 0;
    return Math.min(requested, available);
}

export function hasLotOverConsumption(items: Array<MoveItemRow> | null | undefined): boolean {
    const consumption = buildLotConsumption(items);

    return (items ?? []).some(row => {
        const lotKey = toLotKey(row?.release?.lotNumber);
        if (lotKey === '') return false;
        const takenFromItem = consumption[toRowItemKey(row?.item)] ?? {};
        return takenFromItem[lotKey] > (Number(row?.release?.stockQuantity) || 0);
    });
}

export function hasInvalidConfirmedQuantities(items: Array<MoveItemRow> | null | undefined): boolean {
    const rows = items ?? [];
    const overRowStock = rows.some(row => Number(row?.confirmedQuantity) > (Number(row?.availableQuantity) || 0));
    return overRowStock || hasLotOverConsumption(rows);
}

export function canClearSelection({ hasSelection, cartItemCount }: ClearSelectionArgs): boolean {
    return hasSelection && cartItemCount === 0;
}
